import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {ManagedModelType, ManagedModelTypes} from 'models/ManagedModelDefinitions';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {RequestSampleStatusCounts} from 'requests/fulfillments/FulfillmentConstants';
import {InputReduxActionType} from 'requests/inputs/InputReduxActions';
import {Request} from 'requests/RequestConstants';
import {RequestActionType} from 'requests/RequestReduxActions';
import {RequestTask} from 'requests/tasks/RequestTaskConstants';
import {RequestTaskActionType} from 'requests/tasks/RequestTaskReduxActions';

export class RequestModelSelector {
  constructor(readonly state: BenchlingState) {}

  getRequest(requestId: string): Request | null {
    const {requestsById} = this.state.models.requests.inputs;
    return requestsById[requestId] || null;
  }
}

export class RequestTasksModelSelector {
  constructor(readonly state: BenchlingState) {}

  getModel(modelKey: string): ManagedModelTypes[ManagedModelType.REQUEST_TASKS_BY_REQUEST_ID] | null {
    const {requestTaskIdsByRequestId, requestTasksById} = this.state.models.requests.inputs;
    const requestTaskIds = requestTaskIdsByRequestId[modelKey] || null;
    if (requestTaskIds == null) {
      return null;
    }
    const requestTasks = requestTaskIds.map((id: string) => requestTasksById[id]);
    if (requestTasks.some((requestTask: RequestTask) => requestTask == null)) {
      return null;
    }
    return requestTasks;
  }
}

export interface RequestInputsState {
  // The reason requestTaskIdsByRequestId isn't redundant with requestTasksById (RequestTask has a requestId
  // field) is subtle: a requestId key in requestTaskIdsByRequestId means that *all* tasks of the request are
  // known, whereas only having objects in requestTasksById may mean we only fetched some of them as part of
  // another view (e.g. the fulfillments table in an entry).
  requestTaskIdsByRequestId: {[requestId: string]: Array<string>};
  requestedTaskSchemaIdsByRequestId: {[requestId: string]: Array<string>};
  requestTasksById: {[id: string]: RequestTask};
  requestsById: {[id: string]: Request};
  sampleGroupIdsByRequestId: {[requestId: string]: Array<string>};
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  sampleStatusCountsByRequestId: {[requestId: string]: RequestSampleStatusCounts};
}

export const INPUTS_INITIAL_STATE: RequestInputsState = {
  requestTaskIdsByRequestId: {},
  requestedTaskSchemaIdsByRequestId: {},
  requestTasksById: {},
  requestsById: {},
  sampleGroupIdsByRequestId: {},
  sampleGroupIdsByRequestTaskId: {},
  sampleStatusCountsByRequestId: {},
};

export default function reduceInputs(
  state: RequestInputsState = INPUTS_INITIAL_STATE,
  action: BenchlingAction
): RequestInputsState {
  switch (action.type) {
    case RequestActionType.REQUESTS_FETCH_COMPLETED: {
      const {requests} = action;
      const newRequestsById = _.object(requests.map((request) => [request.id, request]));
      return reactUpdate(state, {
        requestsById: {$merge: newRequestsById},
      });
    }

    case RequestActionType.REQUEST_LIST_V2_COMPLETED: {
      const {requests, sampleStatusCountsByRequestId, requestedTaskSchemaIdsByRequestId} = action;
      const newRequestsById = _.object(requests.map((request) => [request.id, request]));
      return reactUpdate(state, {
        requestsById: {$merge: newRequestsById},
        sampleStatusCountsByRequestId: {$merge: sampleStatusCountsByRequestId},
        requestedTaskSchemaIdsByRequestId: {$merge: requestedTaskSchemaIdsByRequestId},
      });
    }

    case InputReduxActionType.REQUEST_CREATE_COMPLETED: {
      const {request} = action;
      return reactUpdate(state, {
        requestsById: {[request.id]: {$set: request}},
      });
    }

    case InputReduxActionType.REQUEST_SAMPLE_GROUPS_FETCH_COMPLETED: {
      const {sampleGroupIdsByRequestId} = action;
      return reactUpdate(state, {
        sampleGroupIdsByRequestId: {$merge: sampleGroupIdsByRequestId},
      });
    }

    case RequestTaskActionType.REQUEST_TASKS_FETCHED: {
      const {requestTasks} = action;
      return reactUpdate(state, {
        requestTasksById: {$merge: _.indexBy(requestTasks, 'id')},
      });
    }

    case RequestTaskActionType.REQUEST_TASKS_FETCHED_FOR_REQUESTS: {
      const {taskIdsByRequestId, sampleGroupIdsByRequestTaskId} = action;

      return reactUpdate(state, {
        requestTaskIdsByRequestId: {$merge: taskIdsByRequestId},
        sampleGroupIdsByRequestTaskId: {$merge: sampleGroupIdsByRequestTaskId},
      });
    }

    case RequestTaskActionType.REQUEST_TASK_CREATED: {
      const {requestTask, requestTaskIdsByRequestId} = action;
      return reactUpdate(state, {
        requestTaskIdsByRequestId: {$merge: requestTaskIdsByRequestId},
        requestTasksById: {[requestTask.id]: {$set: requestTask}},
        sampleGroupIdsByRequestTaskId: {[requestTask.id]: {$set: []}},
      });
    }

    case RequestTaskActionType.REQUEST_TASK_UPDATED: {
      const {updatedRequestTask} = action;
      return reactUpdate(state, {requestTasksById: {[updatedRequestTask.id]: {$set: updatedRequestTask}}});
    }

    case RequestTaskActionType.REQUEST_TASK_DELETED: {
      const {requestTaskId} = action;
      return reactUpdate(state, {
        requestTaskIdsByRequestId: {
          $apply: (requestTaskIdsByRequestId: {[requestId: string]: Array<string>}) =>
            _.mapObject(requestTaskIdsByRequestId, (requestTaskIds) =>
              // eslint-disable-next-line no-restricted-syntax
              _.without(requestTaskIds, requestTaskId)
            ) as unknown as {[requestId: string]: Array<string>},
        },
        requestTasksById: {
          $apply: (requestTasksById: {[requestTaskId: string]: RequestTask}) =>
            // eslint-disable-next-line no-restricted-syntax
            _.omit(requestTasksById, requestTaskId),
        },
        sampleGroupIdsByRequestTaskId: {
          $apply: (sampleGroupIdsByRequestTaskId: {[requestTaskId: string]: Array<string>}) =>
            // eslint-disable-next-line no-restricted-syntax
            _.omit(sampleGroupIdsByRequestTaskId, requestTaskId),
        },
      });
    }

    case RequestTaskActionType.REQUEST_TASK_SAMPLE_GROUP_ASSOCIATIONS_UPDATED: {
      const {sampleGroupIdsByRequestTaskId} = action;
      return {
        ...state,
        sampleGroupIdsByRequestTaskId: {
          ...state.sampleGroupIdsByRequestTaskId,
          ...sampleGroupIdsByRequestTaskId,
        },
      };
    }

    default:
      return state;
  }
}
